import { Form, Space, Typography } from 'antd';
import { Formik } from 'formik';
import { useCallback } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { type FinderVehicleSpecsFragment } from '../../../../api/fragments/FinderVehicleSpecs';
import TextAreaField from '../../../../components/fields/TextAreaField';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';
import useParse from './useParse';

const validator = validators.compose(validators.requiredString('raw'));

type FormValues = { raw: string };

const initialValues: FormValues = {
    raw: '',
};

type AddLtaFormProps = {
    onComplete: (lta?: Partial<FinderVehicleSpecsFragment['lta']>) => void;
};

const link = 'https://vrl.lta.gov.sg/lta/vrl/action/pubfunc?ID=EnquireRebateBeforeDeReg';

const AddLtaForm = ({ onComplete }: AddLtaFormProps) => {
    const { t } = useTranslation(['finderVehicle']);

    const validate = useValidator(validator);

    const parse = useParse();

    const onSubmit = useCallback(
        (values: FormValues) => {
            const parsed = parse(values.raw);
            // close the drawer and pass the data
            onComplete(parsed);
        },
        [onComplete, parse]
    );

    return (
        <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="addLta" layout="vertical" name="addLta" onSubmitCapture={handleSubmit}>
                    <Space direction="vertical">
                        <Typography.Paragraph>
                            <Trans
                                i18nKey="finderVehicle:addLtaDrawer.note"
                                values={{ link: 'LTA Vehicle Enquiry site' }}
                            >
                                Please proceed to
                                <a href={link} rel="noreferrer" target="_blank">
                                    LTA Vehicle Enquiry site
                                </a>
                                to retrieve the data for the vehicle you want to insert. When viewing the Vehicle Data
                                screen, select all and copy the data and paste into the field below.
                            </Trans>
                        </Typography.Paragraph>
                        <TextAreaField
                            {...t('finderVehicle:addLtaDrawer.fields.raw', {
                                returnObjects: true,
                            })}
                            autoSize={{ minRows: 10, maxRows: 10 }}
                            name="raw"
                        />
                    </Space>
                </Form>
            )}
        </Formik>
    );
};

export default AddLtaForm;
